import { useQuery } from '@tanstack/react-query';

import { packagingApi } from 'api/packaging';

export const packagingPurchaseOrderEntityQueryKeys = {
    all: ['packagingPurchaseOrderEntity'],
    detail: (id) => ['packagingPurchaseOrderEntity', 'detail', id],
    items: (id) => ['packagingPurchaseOrderEntity', 'items', id],
};

export const packagingOptionsQueryKey = ['packaging', 'options'];

export function normalizePackagingOptionsResponse(response) {
    if (response?.status === false) {
        throw new Error(response?.msg ?? 'Unable to load packaging options');
    }
    const rows = response?.data?.rows ?? [];
    return rows
        .map((row) => {
            const model = row?.model;
            if (!model) return null;

            const companySources = row?.companySources ?? model?.companySources;
            const modelWithPartners = companySources !== undefined ? { ...model, companySources } : model;

            return {
                label: `SKU ${model.masterSKU}: ${model.description}`,
                model: modelWithPartners,
                value: String(model.id),
            };
        })
        .filter(Boolean);
}

export const usePackagingOptions = ({ enabled = true } = {}) =>
    useQuery({
        queryKey: packagingOptionsQueryKey,
        queryFn: async () => {
            const response = await packagingApi.getPackaging();
            return normalizePackagingOptionsResponse(response);
        },
        enabled,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
    });

function normalizeEntityResponse(response) {
    const data = response?.data;
    if (data == null) return null;
    return data?.output ?? data;
}

export const usePackagingPurchaseOrderItems = ({ enabled = true, id } = {}) =>
    useQuery({
        queryKey: packagingPurchaseOrderEntityQueryKeys.items(id),
        queryFn: async () => {
            const response = await packagingApi.getPurchaseOrderEntityV2(id);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch packaging purchase order');
            }
            return normalizeEntityResponse(response);
        },
        enabled: Boolean(id) && enabled,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        staleTime: 30 * 1000,
    });
